<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import CircuitPicker from '@/components/ui/CircuitPicker.vue'
import SidebarMenu from '@/components/ui/SidebarMenu.vue'
import { useCircuit } from '@/composables/useCircuit'
import { reachableMenu } from '@/navigation/screenTargets'
import { useMqttStore } from '@/stores/mqtt'
import { useUserStore } from '@/stores/user'
import {
  config,
  getDeviceIdentifier,
  login,
  logout,
  project,
} from '@/composables/useMqttConnection'
import { toColor, translate } from '@/utils'
import { useCustomRouter } from '@/composables/useCustomRouter'
import { useUiStore } from '@/stores/ui'
import type { IDatapoint } from '@/interfaces/IDatapoint'
import type { ISidebarItem } from '@/interfaces/gui'
import { formatInTimeZone } from 'date-fns-tz'
import { USER_LEVELS } from '@/constants/userLevels'
import { APP_ENV_LABEL } from '@/constants/app-env'

const mqttStore = useMqttStore()
const uiStore = useUiStore()
const userStore = useUserStore()
const { navigateTo } = useCustomRouter()
const { currentCircuit, pageRoute } = useCircuit()
const appVersion = __APP_VERSION__
const appEnvLabel = APP_ENV_LABEL

// The login dialog; a locked control opens it too.
const { loginOpen: showLoginModal } = storeToRefs(uiStore)
const username = ref('')
const password = ref('')
const loginError = ref('')
const loginInProgress = ref(false)

// Add computed property for available users
const availableUsers = computed(() => {
  return project.value?.users || []
})

// Add login function
const handleLogin = () => {
  if (!username.value || !password.value) {
    loginError.value = 'please_enter_both_username_and_password'
    return
  }

  loginError.value = ''
  loginInProgress.value = true
  login(username.value, password.value, userStore.sessionId)
}

// Add logout function
const handleLogout = () => {
  logout(userStore.sessionId)
}

// Listen for login results
const setupLoginListener = () => {
  window.addEventListener('login-result', ((event: CustomEvent) => {
    loginInProgress.value = false
    if (event.detail.success) {
      // Login successful
      username.value = ''
      password.value = ''
      showLoginModal.value = false
    } else {
      // Login failed
      loginError.value = event.detail.errorMsg || 'login_failed'
    }
  }) as EventListener)
}

// The menu of the circuit in force, as far as it leads to pages dinoConnect can show.
const menuItems = computed<ReadonlyArray<ISidebarItem>>(() => {
  const sidebar = currentCircuit.value?.sidebar
  return reachableMenu((sidebar && config.value?.frontend?.sidebars?.[sidebar]) || [])
})
const circuitLabel = computed(() => translate(currentCircuit.value?.label || ''))
const title = computed(() => {
  if (uiStore.screenName) {
    return translate(uiStore.screenName)
  }
  return circuitLabel.value
})
const showHomeIcon = computed(() => {
  if (!currentCircuit.value) {
    return false
  }
  return title.value !== circuitLabel.value
})

const currentTime = ref('')

// Update time every second with timezone consideration
const updateTime = () => {
  const now = new Date()

  // Use project timezone if available, fallback to browser's timezone
  const timezone = project.value?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  currentTime.value = formatInTimeZone(now, timezone, 'HH:mm')
}

const isMenuOpen = ref(false)
const showAdminModal = ref(false)

// Add this ref to store the raw icon value
const currentMessageIconValue = ref('ok') // Default value

onMounted(() => {
  // Initial time update
  updateTime()
  // Set up interval for updating time
  const timeInterval = setInterval(updateTime, 1000)

  // Setup login result listener
  setupLoginListener()

  // Update the subscription to set the ref value
  mqttStore.subscribe(`${getDeviceIdentifier()}/datapoints/system/messageIcon`, (newValue) => {
    const dpValue = newValue as IDatapoint
    currentMessageIconValue.value = String(dpValue.value)
  })

  // Clean up interval when component unmounts
  onUnmounted(() => {
    clearInterval(timeInterval)
  })
})

function handleNavigation(route: string) {
  isMenuOpen.value = false // Close menu before navigation
  showAdminModal.value = false // Close admin modal before navigation
  navigateTo(route)
}

function navigateHome() {
  isMenuOpen.value = false // Close menu before navigation
  showAdminModal.value = false // Close admin modal before navigation
  if (currentCircuit.value) {
    navigateTo(`/${currentCircuit.value.startscreen}`)
  }
}

// App pages open inside the circuit in force, so it stays in force while they are shown.
function openCircuitPage(page: string) {
  isMenuOpen.value = false // Close menu before navigation
  showAdminModal.value = false // Close admin modal before navigation
  uiStore.requestMessageScope(undefined) // Pages opened from the menu use their default scope
  if (currentCircuit.value) {
    navigateTo(pageRoute(page))
  }
}

const navigateMessages = () => openCircuitPage('messagePage')
const navigateToFileManager = () => openCircuitPage('filemanagerPage')
const navigateToAdminPanel = () => openCircuitPage('adminPanelPage')
const navigateToSetupWizard = () => openCircuitPage('setupWizardPage')
const navigateToDpViewer = () => openCircuitPage('dpviewerPage')

// Update the computed property to use the ref
const messageIcon = computed(() => {
  const iconValue = currentMessageIconValue.value

  switch (iconValue.toLowerCase()) {
    case 'fault':
      return { icon: 'f5f9', color: toColor('red') }
    case 'alarm':
      return { icon: 'f5f7', color: toColor('red') }
    case 'service':
      return { icon: 'f4f3', color: toColor('blue') }
    case 'warning':
      return { icon: 'f3fb', color: toColor('yellow') }
    case 'ok':
    default:
      return { icon: 'efa3', color: toColor('green') }
  }
})

// Add computed property for user icon based on level
const userIcon = computed(() => {
  const level = userStore.userLevel

  if (level >= USER_LEVELS.SUPERUSER) return 'f473'
  if (level > USER_LEVELS.DEFAULT) return 'f47b'
  return 'f474'
})

// Computed property to check if admin controls should be shown
const showAdminControls = computed(() => {
  return userStore.userLevel >= USER_LEVELS.ADMIN
})
</script>

<template>
  <div class="vue-component-border" data-component-name="Navbar">
    <nav class="fixed top-0 left-0 right-0 h-[60px] bg-white shadow-md z-100">
      <div
        class="max-w-[100vw] w-full h-full flex items-center justify-between overflow-x-hidden px-5"
      >
        <!-- Menu button and Title wrapper -->
        <div class="flex items-center gap-2 flex-grow min-w-0">
          <!-- Menu button -->
          <button
            aria-label="Toggle navigation"
            class="p-2 rounded-md hover:bg-slate-100 transition-all duration-200 flex-shrink-0"
            @click="isMenuOpen = !isMenuOpen"
          >
            <div :class="{ open: isMenuOpen }" class="w-6 h-5 relative cursor-pointer">
              <span
                :class="{ 'rotate-45 top-[9px]': isMenuOpen, 'top-0': !isMenuOpen }"
                class="absolute h-0.5 w-full bg-slate-500 rounded transform transition-all duration-300 left-0"
              >
              </span>
              <span
                :class="{ 'opacity-0': isMenuOpen }"
                class="absolute h-0.5 w-full bg-slate-500 rounded top-[9px] transition-all duration-300 left-0"
              >
              </span>
              <span
                :class="{ '-rotate-45 top-[9px]': isMenuOpen, 'top-[18px]': !isMenuOpen }"
                class="absolute h-0.5 w-full bg-slate-500 rounded transform transition-all duration-300 left-0"
              >
              </span>
            </div>
          </button>

          <!-- Title -->
          <span class="text-xl font-semibold text-slate-800 truncate flex-grow">{{ title }}</span>
        </div>

        <!-- Clock, user info, and icons -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <span class="text-xl font-semibold text-slate-500 hidden sm:block">{{
            currentTime
          }}</span>
          <button
            v-if="showHomeIcon"
            class="rounded-md hover:bg-slate-100 transition-all duration-200"
            @click="navigateHome"
          >
            <span
              class="font-icomoon icon text-2xl text-slate-500 flex items-center justify-center w-8 h-8 flex-shrink-0"
              >&#xE954;</span
            >
          </button>

          <!-- Admin Controls Button -->
          <button
            v-if="showAdminControls"
            class="rounded-md hover:bg-slate-100 transition-all duration-200"
            @click="showAdminModal = true"
          >
            <span
              class="font-icomoon icon text-2xl text-slate-500 flex items-center justify-center w-8 h-8 flex-shrink-0"
              >&#xe908;</span
            >
          </button>

          <button
            class="rounded-md hover:bg-slate-100 transition-all duration-200"
            @click="navigateMessages"
          >
            <span
              :style="{ color: messageIcon.color }"
              class="font-icomoon icon text-2xl flex items-center justify-center w-8 h-8 flex-shrink-0"
            >
              {{ String.fromCharCode(parseInt(messageIcon.icon, 16)) }}
            </span>
          </button>
        </div>

        <!-- Sidebar Menu -->
        <div
          :class="[isMenuOpen ? 'left-0' : '-left-[100vw]']"
          class="fixed top-[60px] h-[calc(100dvh-60px)] w-[min(300px,100vw)] bg-white transition-all duration-300 z-50"
        >
          <div class="p-3 sm:p-5 h-full overflow-y-auto flex flex-col">
            <!-- User info or login button in sidebar -->
            <div v-if="userStore.userLevel && userStore.userLevel >= 0" class="mb-2">
              <div class="w-full flex items-center gap-4 p-3 rounded-md transition-all">
                <span
                  class="font-icomoon icon text-2xl text-slate-500 flex items-center justify-center w-8 h-8 flex-shrink-0"
                >
                  {{ String.fromCharCode(parseInt(userIcon, 16)) }}
                </span>
                <div class="flex flex-col flex-grow">
                  <span class="text-base text-slate-800 text-left">{{
                    translate(userStore.userName)
                  }}</span>
                  <span class="text-sm text-slate-500"
                    >{{ translate('User Level') }}: {{ userStore.userLevel }}</span
                  >
                </div>
                <button
                  v-if="userStore.userLevel !== USER_LEVELS.ADMIN"
                  class="ml-2 p-2 rounded-md bg-slate-100 hover:bg-slate-200 transition-colors"
                  @click="handleLogout"
                >
                  <span class="font-icomoon icon text-lg text-slate-600">&#xe961;</span>
                </button>
              </div>
            </div>
            <div v-else class="mb-2">
              <button
                class="w-full flex items-center gap-4 p-3 rounded-md bg-blue-50 hover:bg-blue-100 transition-all"
                @click="showLoginModal = true"
              >
                <span
                  class="font-icomoon icon text-2xl text-blue-500 flex items-center justify-center w-8 h-8"
                >
                  &#xe960;
                </span>
                <span class="text-base text-blue-700 flex-1 text-left">{{
                  translate('#login')
                }}</span>
              </button>
            </div>

            <!-- Divider -->
            <div class="h-px bg-slate-200 my-4"></div>

            <CircuitPicker @chosen="isMenuOpen = false" />

            <!-- A new circuit brings its own menu, opened at its top level. -->
            <SidebarMenu
              :key="currentCircuit?.name"
              class="flex-1"
              :items="menuItems"
              @navigate="handleNavigation"
            />

            <div
              v-if="showAdminControls"
              class="pt-4 pb-1 text-[11px] leading-none text-slate-400 text-center"
            >
              v{{ appVersion }}
              <span v-if="appEnvLabel" class="font-semibold text-amber-600">
                · {{ appEnvLabel }}
              </span>
            </div>
          </div>
        </div>

        <!-- Overlay -->
        <div
          v-if="isMenuOpen"
          class="fixed top-[60px] left-0 right-0 bottom-0 bg-black/50 z-40"
          @click="isMenuOpen = false"
        />
      </div>
    </nav>

    <!-- Login Modal -->
    <div v-if="showLoginModal" class="fixed inset-0 flex items-center justify-center z-[1000]">
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        @click="showLoginModal = false"
      ></div>
      <div
        class="bg-white rounded-xl shadow-2xl w-[450px] max-w-[90vw] z-[1001] relative transform transition-all duration-300 scale-100 opacity-100"
      >
        <div class="p-6 border-b border-gray-100 flex items-center justify-between">
          <h3 class="text-xl font-semibold text-gray-800">{{ translate('#login') }}</h3>
          <button
            @click="showLoginModal = false"
            class="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div class="p-6">
          <form @submit.prevent="handleLogin" class="space-y-5">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2" for="username">
                {{ translate('#username') }}
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="text-gray-500 icon font-icomoon text-sm">&#xf445;</span>
                </div>
                <select
                  id="username"
                  v-model="username"
                  class="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                  required
                >
                  <option value="" disabled>{{ translate('#select_a_user') }}</option>
                  <option v-for="user in availableUsers" :key="user.name" :value="user.name">
                    {{ user.name }}
                  </option>
                </select>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2" for="password">
                {{ translate('#password') }}
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span class="text-gray-500 icon font-icomoon text-sm">&#xefc0;</span>
                </div>
                <input
                  id="password"
                  v-model="password"
                  type="password"
                  class="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                  :placeholder="translate('#enter_your_password')"
                  required
                />
              </div>
            </div>
            <div
              v-if="loginError"
              class="p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm"
            >
              <div class="flex gap-2 items-center">
                <span class="font-icomoon icon text-red-500 text-xs">&#xf3fb;</span>
                <span>{{ translate('#' + loginError) }}</span>
              </div>
            </div>
            <div class="pt-2 flex justify-end gap-3">
              <button
                type="button"
                class="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
                @click="showLoginModal = false"
                :disabled="loginInProgress"
              >
                {{ translate('#cancel') }}
              </button>
              <button
                type="submit"
                class="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                :disabled="loginInProgress"
              >
                <span
                  v-if="loginInProgress"
                  class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"
                ></span>
                {{ translate('#login') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Admin Controls Modal -->
    <div v-if="showAdminModal" class="fixed inset-0 flex items-center justify-center z-[1000] p-4">
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        @click="showAdminModal = false"
      ></div>
      <div
        class="bg-white rounded-xl shadow-2xl w-full max-w-[400px] sm:max-w-[450px] z-[1001] relative transform transition-all duration-300 scale-100 opacity-100 max-h-[90dvh] overflow-hidden"
      >
        <div class="p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between">
          <h3 class="text-lg sm:text-xl font-semibold text-gray-800">
            {{ translate('#admin_controls') }}
          </h3>
          <button
            @click="showAdminModal = false"
            class="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 sm:h-6 sm:w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div class="p-4 sm:p-6 overflow-y-auto">
          <div class="space-y-3 sm:space-y-4">
            <!-- File Manager -->
            <button
              class="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
              @click="navigateToFileManager"
            >
              <div
                class="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center"
              >
                <span class="font-icomoon icon text-xl sm:text-2xl text-blue-600">&#xEDEC;</span>
              </div>
              <div class="flex-grow text-left min-w-0">
                <h4 class="text-base sm:text-lg font-medium text-gray-900 truncate">
                  {{ translate('#filemanager') }}
                </h4>
                <p class="text-xs sm:text-sm text-gray-500 line-clamp-2">
                  {{ translate('#filemanager_description') }}
                </p>
              </div>
              <div class="flex-shrink-0">
                <svg
                  class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </button>

            <!-- Admin Panel -->
            <button
              class="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
              @click="navigateToAdminPanel"
            >
              <div
                class="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center"
              >
                <span class="font-icomoon icon text-xl sm:text-2xl text-green-600">&#xe908;</span>
              </div>
              <div class="flex-grow text-left min-w-0">
                <h4 class="text-base sm:text-lg font-medium text-gray-900 truncate">
                  {{ translate('#adminPanelPage') }}
                </h4>
                <p class="text-xs sm:text-sm text-gray-500 line-clamp-2">
                  {{ translate('#admin_panel_description') }}
                </p>
              </div>
              <div class="flex-shrink-0">
                <svg
                  class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </button>

            <!-- Setup Wizard -->
            <button
              class="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
              @click="navigateToSetupWizard"
            >
              <div
                class="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center"
              >
                <span class="font-icomoon icon text-xl sm:text-2xl text-purple-600">&#xeea5;</span>
              </div>
              <div class="flex-grow text-left min-w-0">
                <h4 class="text-base sm:text-lg font-medium text-gray-900 truncate">
                  {{ translate('#setup_wizard') }}
                </h4>
                <p class="text-xs sm:text-sm text-gray-500 line-clamp-2">
                  {{ translate('#setup_wizard_description') }}
                </p>
              </div>
              <div class="flex-shrink-0">
                <svg
                  class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </button>

            <!-- Datapoint Viewer -->
            <button
              class="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
              @click="navigateToDpViewer"
            >
              <div
                class="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center"
              >
                <span class="font-icomoon icon text-xl sm:text-2xl text-orange-600">&#xeb01;</span>
              </div>
              <div class="flex-grow text-left min-w-0">
                <h4 class="text-base sm:text-lg font-medium text-gray-900 truncate">
                  {{ translate('#datapoint_viewer') }}
                </h4>
                <p class="text-xs sm:text-sm text-gray-500 line-clamp-2">
                  {{ translate('#datapoint_viewer_description') }}
                </p>
              </div>
              <div class="flex-shrink-0">
                <svg
                  class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
